import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Calendar, Save, Download, Share2, Plus, Star, Mic, MicOff, Printer, X, Trash2, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_ITEMS = {
  '₹5': [{ name: 'Orange', price: 5 }],
  '₹10': [
    { name: 'Cup', price: 10 },
    { name: 'Chocobar', price: 10 },
    { name: 'Orange', price: 10 },
    { name: 'Mango', price: 10 },
    { name: 'Mango Dooz', price: 10 },
    { name: 'Lemon', price: 10 },
    { name: 'Jumbo', price: 10 },
    { name: 'Kulfi', price: 10 },
    { name: 'Rabri Kulfi', price: 10 },
    { name: 'Chocolate Dudh', price: 10 },
    { name: 'Tiranga', price: 10 },
  ],
  '₹20': [
    { name: 'Cone (Butterscotch)', price: 20 },
    { name: 'Malai Kulfi', price: 20 },
    { name: 'Feast', price: 20 },
  ],
  '₹25': [{ name: 'Matka Kulfi', price: 25 }],
  '₹30': [{ name: 'Cone (Chocolate)', price: 30 }],  // Changed from "Cone Special"
  '₹80': [{ name: 'Container Ice Cream', price: 80 }],
  '₹90': [{ name: 'Break (Family Pack)', price: 90 }],
};

// Bazaar Items - separate from invoice calculations
const BAZAAR_ITEMS = [
  { name: 'Orange', price: 5 },
  { name: 'Campa', price: 5 },
  { name: 'Mango', price: 5 },
];

// Flavour options for ₹90 items
const FLAVOUR_OPTIONS = ['Vanilla', 'Butterscotch', 'Strawberry'];

export default function InvoiceGenerator() {
  const [vendors, setVendors] = useState([]);
  const [vendorName, setVendorName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [amountSubmitted, setAmountSubmitted] = useState('');
  const [upiAmount, setUpiAmount] = useState('');  // New: UPI/Paytm amount
  const [commissionType, setCommissionType] = useState('taken');
  const [items, setItems] = useState({});
  const [bazaarItems, setBazaarItems] = useState({});  // New: Bazaar items
  const [itemFlavours, setItemFlavours] = useState({});  // New: ₹90 item flavours
  const [customItems, setCustomItems] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [calculations, setCalculations] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [stockAdjustment, setStockAdjustment] = useState('');
  const [showDateChangeModal, setShowDateChangeModal] = useState(false);  // New: Date change modal
  const [newInvoiceDate, setNewInvoiceDate] = useState('');  // New: For date change
  const recognitionRef = useRef(null);

  useEffect(() => {
    fetchVendors();
    fetchCustomItems();
    initializeSpeechRecognition();
  }, []);

  useEffect(() => {
    if (amountSubmitted) {
      calculateInvoice();
    }
  }, [amountSubmitted, commissionType, items, stockAdjustment, bazaarItems, upiAmount, itemFlavours]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const deleteVendor = async (vendorId, vendorName) => {
    try {
      await axios.delete(`${API}/vendors/${vendorId}`);
      toast.success(`"${vendorName}" deleted!`);
      await fetchVendors();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete. Please try again.');
    }
  };

  const handleVendorNameChange = async (value) => {
    setVendorName(value);
    
    // Auto-save vendor if it's a new name and has at least 2 characters
    if (value.trim().length >= 2) {
      const existingVendor = vendors.find(
        v => v.name.toLowerCase().trim() === value.trim().toLowerCase()
      );
      
      if (!existingVendor) {
        // Debounced save - will save after user stops typing
        clearTimeout(window.vendorSaveTimeout);
        window.vendorSaveTimeout = setTimeout(async () => {
          // Double-check before saving (vendor list might have been updated)
          const latestCheck = vendors.find(
            v => v.name.toLowerCase().trim() === value.trim().toLowerCase()
          );
          
          // Only save complete names: must have space OR be at least 5 chars
          const hasSpace = value.trim().includes(' ');
          const isLongEnough = value.trim().length >= 5;
          
          if (!latestCheck && value.trim() && (hasSpace || isLongEnough)) {
            try {
              const response = await axios.post(`${API}/vendors`, { name: value.trim() });
              const updatedVendors = await axios.get(`${API}/vendors`);
              setVendors(updatedVendors.data);
              toast.success(`"${value.trim()}" saved to vendor list`);
            } catch (error) {
              console.error('Error auto-saving vendor:', error);
            }
          }
        }, 4000); // Increased to 4 seconds for complete names
      }
    }
  };

  const fetchCustomItems = async () => {
    try {
      // Fetch ALL custom items (not just starred)
      const response = await axios.get(`${API}/custom-items`);
      setCustomItems(response.data);
      console.log('Custom items loaded:', response.data);
    } catch (error) {
      console.error('Error fetching custom items:', error);
    }
  };

  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-IN';
      recognitionRef.current.maxAlternatives = 1;

      recognitionRef.current.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setVendorName(transcript);
        setIsListening(false);
        toast.success('✓ Voice captured: ' + transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        let errorMessage = '';
        switch(event.error) {
          case 'no-speech':
            errorMessage = 'No speech detected. Please speak clearly and try again.';
            break;
          case 'audio-capture':
            errorMessage = 'Microphone not found. Please check if microphone is connected.';
            break;
          case 'not-allowed':
            errorMessage = 'Microphone blocked! Please allow microphone access in browser settings.';
            break;
          case 'network':
            errorMessage = 'Speech recognition works offline. Please check microphone permissions.';
            break;
          case 'aborted':
            errorMessage = 'Voice input cancelled.';
            break;
          default:
            errorMessage = 'Could not capture voice. Please try typing instead.';
        }
        if (errorMessage) {
          toast.error(errorMessage);
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  };

  const toggleVoiceInput = async () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      toast.error('Voice input not supported. Please use Chrome browser.');
      return;
    }

    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    try {
      // Check microphone permission first
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'microphone' });
        if (permission.state === 'denied') {
          toast.error('Microphone blocked! Please allow microphone in browser settings.');
          return;
        }
      }

      if (!recognitionRef.current) {
        initializeSpeechRecognition();
      }

      if (recognitionRef.current) {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info('🎤 Listening... Speak vendor name now', { duration: 3000 });
      }
    } catch (error) {
      console.error('Failed to start recognition:', error);
      setIsListening(false);
      
      if (error.message && error.message.includes('already started')) {
        toast.warning('Voice input is already active');
      } else {
        toast.error('Could not start microphone. Please check browser permissions.');
      }
    }
  };

  const calculateInvoice = () => {
    const amount = parseFloat(amountSubmitted) || 0;
    const adjustment = parseFloat(stockAdjustment) || 0;
    
    let totalSales, vendorCommission, factoryShare, stockLimit;

    if (commissionType === 'taken') {
      totalSales = amount / 0.7;
      vendorCommission = totalSales * 0.3;
      factoryShare = amount;
      stockLimit = totalSales;
    } else {
      totalSales = amount;
      vendorCommission = amount * 0.3;
      factoryShare = amount * 0.7;
      stockLimit = amount;
    }

    // Apply adjustment (previous day free items)
    const adjustedStockLimit = stockLimit + adjustment;

    const itemsList = [];
    let totalStockValue = 0;

    Object.entries(items).forEach(([key, quantity]) => {
      if (quantity > 0) {
        const [itemName, priceStr] = key.split('|');
        const price = parseInt(priceStr);
        const total = quantity * price;
        // Include flavour for ₹90 items
        const flavour = price === 90 ? itemFlavours[key] : null;
        itemsList.push({ item_name: itemName, quantity, price, total, flavour });
        totalStockValue += total;
      }
    });

    // Get bazaar items list (for display only, not affecting calculations)
    const bazaarItemsList = [];
    Object.entries(bazaarItems).forEach(([key, quantity]) => {
      if (quantity > 0) {
        const [itemName, priceStr] = key.split('|');
        const price = parseInt(priceStr);
        const total = quantity * price;
        bazaarItemsList.push({ item_name: itemName, quantity, price, total });
      }
    });

    const balance = adjustedStockLimit - totalStockValue;

    setCalculations({
      totalSales,
      vendorCommission,
      factoryShare,
      stockLimit,
      adjustment,
      adjustedStockLimit,
      itemsList,
      bazaarItemsList,  // Add bazaar items for display
      totalStockValue,
      balance,
      upiAmount: parseFloat(upiAmount) || 0,  // Store UPI amount
      cashAmount: amount - (parseFloat(upiAmount) || 0),  // Calculate cash amount
    });
  };

  const handleItemQuantityChange = (itemName, price, quantity) => {
    const key = `${itemName}|${price}`;
    setItems((prev) => ({
      ...prev,
      [key]: parseInt(quantity) || 0,
    }));
  };

  // Handle bazaar item quantity (separate from invoice)
  const handleBazaarItemChange = (itemName, price, quantity) => {
    const key = `${itemName}|${price}`;
    setBazaarItems((prev) => ({
      ...prev,
      [key]: parseInt(quantity) || 0,
    }));
  };

  // Handle flavour selection for ₹90 items
  const handleFlavourChange = (itemName, price, flavour) => {
    const key = `${itemName}|${price}`;
    setItemFlavours((prev) => ({
      ...prev,
      [key]: flavour,
    }));
  };

  // Save invoice with different date (creates a copy with the new date)
  const saveWithDifferentDate = async () => {
    if (!newInvoiceDate) {
      toast.error('Please select a new date');
      return;
    }
    
    if (!vendorName || !amountSubmitted || !calculations) {
      toast.error('Invoice data is missing');
      return;
    }

    try {
      let vendor = vendors.find((v) => v.name === vendorName);
      if (!vendor) {
        const vendorResponse = await axios.post(`${API}/vendors`, { name: vendorName });
        vendor = vendorResponse.data;
        setVendors([...vendors, vendor]);
      }

      const invoiceData = {
        vendor_id: vendor.id,
        vendor_name: vendorName,
        date: newInvoiceDate,
        amount_submitted: parseFloat(amountSubmitted),
        upi_amount: parseFloat(upiAmount) || 0,
        cash_amount: parseFloat(amountSubmitted) - (parseFloat(upiAmount) || 0),
        commission_type: commissionType,
        total_sales: calculations.totalSales,
        vendor_commission: calculations.vendorCommission,
        factory_share: calculations.factoryShare,
        stock_limit: calculations.stockLimit,
        items: calculations.itemsList,
        bazaar_items: calculations.bazaarItemsList,
        total_stock_value: calculations.totalStockValue,
        balance: calculations.balance,
      };

      await axios.post(`${API}/invoices`, invoiceData);
      setShowDateChangeModal(false);
      setNewInvoiceDate('');
      toast.success(`Invoice copy saved with date: ${newInvoiceDate}`);
    } catch (error) {
      toast.error('Error saving invoice with different date');
      console.error(error);
    }
  };

  const addCustomItem = async () => {
    if (!newItemName || !newItemPrice) {
      toast.error('Please enter item name and price');
      return;
    }

    const price = parseInt(newItemPrice);
    if (price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    // Check for duplicate in custom items (case-insensitive)
    const isDuplicateCustom = customItems.some(
      item => item.name.toLowerCase().trim() === newItemName.toLowerCase().trim() && item.price === price
    );
    
    if (isDuplicateCustom) {
      toast.error(`"${newItemName}" at ₹${price} already exists in custom items`);
      return;
    }

    // Check for duplicate in default items (case-insensitive)
    const category = `₹${price}`;
    const defaultCategory = DEFAULT_ITEMS[category] || [];
    const isDuplicateDefault = defaultCategory.some(
      item => item.name.toLowerCase().trim() === newItemName.toLowerCase().trim()
    );
    
    if (isDuplicateDefault) {
      toast.error(`"${newItemName}" already exists in default ₹${price} items`);
      return;
    }

    try {
      // Auto-save to database permanently
      const response = await axios.post(`${API}/custom-items`, {
        name: newItemName,
        price: price,
      });
      
      // Refresh custom items list
      await fetchCustomItems();
      
      toast.success(`"${newItemName}" added permanently to ₹${price} category`);
      setNewItemName('');
      setNewItemPrice('');
    } catch (error) {
      // Handle duplicate error from backend
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error adding custom item');
      }
      console.error(error);
    }
  };

  const deleteCustomItem = async (itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}"? This will remove it permanently.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/custom-items/${itemId}`);
      await fetchCustomItems();
      toast.success(`"${itemName}" deleted`);
    } catch (error) {
      toast.error('Error deleting item');
      console.error(error);
    }
  };

  const saveInvoice = async () => {
    if (!vendorName || !amountSubmitted || !calculations) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      let vendor = vendors.find((v) => v.name === vendorName);
      if (!vendor) {
        const vendorResponse = await axios.post(`${API}/vendors`, { name: vendorName });
        vendor = vendorResponse.data;
        setVendors([...vendors, vendor]);
      }

      const invoiceData = {
        vendor_id: vendor.id,
        vendor_name: vendorName,
        date,
        amount_submitted: parseFloat(amountSubmitted),
        upi_amount: parseFloat(upiAmount) || 0,  // Add UPI amount
        cash_amount: parseFloat(amountSubmitted) - (parseFloat(upiAmount) || 0),  // Add cash amount
        commission_type: commissionType,
        total_sales: calculations.totalSales,
        vendor_commission: calculations.vendorCommission,
        factory_share: calculations.factoryShare,
        stock_limit: calculations.stockLimit,
        items: calculations.itemsList,
        bazaar_items: calculations.bazaarItemsList,  // Add bazaar items
        total_stock_value: calculations.totalStockValue,
        balance: calculations.balance,
      };

      const response = await axios.post(`${API}/invoices`, invoiceData);
      setSavedInvoiceId(response.data.id);
      
      if (calculations.balance < 0) {
        toast.success(`Invoice saved! Note: Stock exceeded by ₹${Math.abs(calculations.balance).toFixed(2)}`);
      } else {
        toast.success('Invoice saved successfully!');
      }
    } catch (error) {
      toast.error('Error saving invoice');
      console.error(error);
    }
  };

  const printInvoice = () => {
    if (!calculations) return;
    
    const printWindow = window.open('', '_blank');
    
    // Calculate dynamic font size based on item count - smaller for more items
    const itemCount = calculations.itemsList.length;
    const baseFontSize = itemCount > 20 ? 8 : itemCount > 15 ? 9 : itemCount > 10 ? 10 : itemCount > 5 ? 11 : 12;
    const itemPadding = itemCount > 15 ? '2px 3px' : itemCount > 10 ? '3px 4px' : '4px 5px';
    const headerSize = itemCount > 15 ? 18 : itemCount > 10 ? 20 : 24;
    const taglineSize = itemCount > 10 ? 9 : 10;
    const sectionMargin = itemCount > 15 ? '4px 0' : itemCount > 10 ? '6px 0' : '8px 0';
    
    const itemsHTML = calculations.itemsList.map((item, idx) => `
      <tr>
        <td style="padding: ${itemPadding}; border-bottom: 1px solid #ddd;">${item.item_name}${item.flavour ? ` (${item.flavour})` : ''}</td>
        <td style="padding: ${itemPadding}; border-bottom: 1px solid #ddd; text-align: center;">${item.quantity} × ₹${item.price}</td>
        <td style="padding: ${itemPadding}; border-bottom: 1px solid #ddd; text-align: right; font-weight: 600;">₹${item.total}</td>
      </tr>
    `).join('');

    // Bazaar items HTML (displayed separately)
    const bazaarItemsHTML = calculations.bazaarItemsList && calculations.bazaarItemsList.length > 0 
      ? calculations.bazaarItemsList.map((item) => `
          <tr>
            <td style="padding: ${itemPadding}; border-bottom: 1px solid #ddd;">${item.item_name}</td>
            <td style="padding: ${itemPadding}; border-bottom: 1px solid #ddd; text-align: center;">${item.quantity} × ₹${item.price}</td>
            <td style="padding: ${itemPadding}; border-bottom: 1px solid #ddd; text-align: right; font-weight: 600;">₹${item.total}</td>
          </tr>
        `).join('')
      : '';
    
    const bazaarTotal = calculations.bazaarItemsList 
      ? calculations.bazaarItemsList.reduce((sum, item) => sum + item.total, 0) 
      : 0;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${vendorName}</title>
          <style>
            @page {
              size: 105mm 148mm;
              margin: 3mm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            html, body {
              width: 105mm;
              min-height: 148mm;
              font-family: Arial, sans-serif;
              font-size: ${baseFontSize}px;
              line-height: 1.2;
              color: #000;
            }
            .invoice-container {
              width: 100%;
              padding: 2mm;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 4px;
              margin-bottom: ${sectionMargin};
            }
            .brand-name {
              font-size: ${headerSize}px;
              font-weight: 800;
              color: #000;
              margin-bottom: 1px;
            }
            .tagline {
              font-size: ${taglineSize}px;
              color: #333;
              font-style: italic;
            }
            .info-section {
              margin-bottom: ${sectionMargin};
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 2px 0;
              font-size: ${baseFontSize}px;
            }
            .info-row .label {
              font-weight: 600;
            }
            .summary-section {
              border: 1px solid #000;
              padding: 4px 6px;
              margin: ${sectionMargin};
              background: #f8f8f8;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 2px 0;
              font-size: ${baseFontSize}px;
            }
            .summary-row .value {
              font-weight: 700;
            }
            .items-section {
              margin: ${sectionMargin};
            }
            .items-title {
              font-weight: 700;
              font-size: ${baseFontSize + 1}px;
              margin-bottom: 3px;
              border-bottom: 1px solid #000;
              padding-bottom: 2px;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              font-size: ${baseFontSize}px;
            }
            .items-table th {
              text-align: left;
              padding: ${itemPadding};
              border-bottom: 1px solid #000;
              font-weight: 700;
              background: #eee;
              font-size: ${baseFontSize - 1}px;
            }
            .items-table th:nth-child(2) {
              text-align: center;
            }
            .items-table th:last-child {
              text-align: right;
            }
            .footer-section {
              border-top: 2px solid #000;
              padding-top: 4px;
              margin-top: ${sectionMargin};
            }
            .footer-row {
              display: flex;
              justify-content: space-between;
              padding: 2px 0;
              font-size: ${baseFontSize + 1}px;
            }
            .footer-row.total {
              font-weight: 700;
              font-size: ${baseFontSize + 2}px;
              border-top: 1px solid #000;
              padding-top: 4px;
              margin-top: 2px;
            }
            .return-notice {
              border: 1px solid #000;
              padding: 4px;
              margin: 4px 0;
              text-align: center;
              font-weight: 700;
              background: #fff3cd;
              font-size: ${baseFontSize}px;
            }
            @media print {
              html, body {
                width: 105mm;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="header">
              <div class="brand-name">Convella</div>
              <div class="tagline">The Taste of Real Ice Cream</div>
            </div>

            <div class="info-section">
              <div class="info-row">
                <span class="label">Vendor:</span>
                <span>${vendorName}</span>
              </div>
              <div class="info-row">
                <span class="label">Date:</span>
                <span>${date}</span>
              </div>
              <div class="info-row">
                <span class="label">Amount Submitted:</span>
                <span>₹${parseFloat(amountSubmitted).toFixed(2)}</span>
              </div>
            </div>

            <div class="summary-section">
              <div class="summary-row">
                <span class="label">Total Sales:</span>
                <span class="value">₹${calculations.totalSales.toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="label">Vendor Commission (30%):</span>
                <span class="value">₹${calculations.vendorCommission.toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="label">Factory Share (70%):</span>
                <span class="value">₹${calculations.factoryShare.toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="label">Stock Limit:</span>
                <span class="value">₹${calculations.stockLimit.toFixed(2)}</span>
              </div>
            </div>

            ${commissionType === 'not_taken' ? `
              <div class="return-notice">
                ⚠ Return ₹${calculations.vendorCommission.toFixed(2)} to vendor
              </div>
            ` : ''}

            <div class="items-section">
              <div class="items-title">Items (${calculations.itemsList.length})</div>
              <table class="items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty × Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemsHTML}
                </tbody>
              </table>
            </div>

            ${bazaarItemsHTML ? `
            <div class="items-section" style="margin-top: 8px; border-top: 1px dashed #999; padding-top: 6px;">
              <div class="items-title" style="font-style: italic; color: #666;">Bazaar Items (Not in Invoice)</div>
              <table class="items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty × Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${bazaarItemsHTML}
                </tbody>
              </table>
              <div style="text-align: right; font-size: ${baseFontSize - 1}px; margin-top: 4px; color: #666;">
                Bazaar Total: ₹${bazaarTotal}
              </div>
            </div>
            ` : ''}

            <div class="footer-section">
              <div class="footer-row">
                <span class="label">Total Stock Value:</span>
                <span>₹${calculations.totalStockValue.toFixed(2)}</span>
              </div>
              <div class="footer-row total" style="color: ${calculations.balance < 0 ? '#dc2626' : '#000'};">
                <span>Remaining Balance:</span>
                <span>₹${calculations.balance.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const downloadPDF = async () => {
    const element = document.getElementById('invoice-preview');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice-${vendorName}-${date}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const shareWhatsApp = () => {
    if (!calculations) return;
    const message = `*Convella Invoice*\n\nVendor: ${vendorName}\nDate: ${date}\nAmount Submitted: ₹${amountSubmitted}\nTotal Sales: ₹${calculations.totalSales.toFixed(2)}\nVendor Commission: ₹${calculations.vendorCommission.toFixed(2)}\nFactory Share: ₹${calculations.factoryShare.toFixed(2)}\nStock Limit: ₹${calculations.stockLimit.toFixed(2)}\nTotal Stock Value: ₹${calculations.totalStockValue.toFixed(2)}\nBalance: ₹${calculations.balance.toFixed(2)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
  };

  const resetForm = () => {
    setVendorName('');
    setDate(new Date().toISOString().split('T')[0]);
    setAmountSubmitted('');
    setUpiAmount('');  // Reset UPI amount
    setCommissionType('taken');
    setItems({});
    setBazaarItems({});  // Reset bazaar items
    setItemFlavours({});  // Reset flavours
    setStockAdjustment('');
    setCalculations(null);
    setSavedInvoiceId(null);
    toast.info('Form reset. Ready for new invoice.');
  };

  // Deep copy DEFAULT_ITEMS to avoid mutating original arrays
  const allItems = {};
  Object.entries(DEFAULT_ITEMS).forEach(([category, categoryItems]) => {
    allItems[category] = [...categoryItems];
  });
  
  // Add custom items to the appropriate categories
  customItems.forEach((item) => {
    const category = item.category;
    if (!allItems[category]) {
      allItems[category] = [];
    }
    allItems[category].push({ name: item.name, price: item.price, id: item.id, isCustom: true });
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-fade-in">
      <div className="lg:col-span-7 space-y-6">
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Invoice Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="vendor-name" className="text-sm font-semibold">
                    Vendor Name
                  </Label>
                  {vendors.length > 0 && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowVendorList(!showVendorList)}
                        className="text-xs text-[#7FA1C3] hover:text-[#6482A6] font-semibold"
                      >
                        {showVendorList ? 'Hide' : 'Manage'} Vendors ({vendors.length})
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    id="vendor-name"
                    data-testid="vendor-name-input"
                    list="vendors"
                    value={vendorName}
                    onChange={(e) => handleVendorNameChange(e.target.value)}
                    placeholder="Enter or select vendor"
                    className="input-focus flex-1"
                  />
                  <Button
                    type="button"
                    onClick={toggleVoiceInput}
                    data-testid="voice-input-button"
                    className={`px-3 rounded-full transition-all ${
                      isListening
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                        : 'bg-[#7FA1C3] hover:bg-[#6482A6]'
                    } text-white shadow-md`}
                    title={isListening ? 'Stop listening' : 'Click and speak vendor name'}
                  >
                    {isListening ? <MicOff size={18} /> : <Mic size={18} />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  💡 Click microphone button and speak clearly. If it doesn't work, please allow microphone access in your browser settings.
                </p>
                <datalist id="vendors">
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.name} />
                  ))}
                </datalist>
                
                {showVendorList && vendors.length > 0 && (
                  <div className="mt-3 p-3 bg-white border-2 border-slate-300 rounded-lg max-h-48 overflow-y-auto shadow-md">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Saved Vendors:</p>
                    <div className="space-y-1">
                      {vendors.map((vendor) => (
                        <div
                          key={vendor.id}
                          className="flex items-center justify-between p-2 hover:bg-slate-50 rounded"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setVendorName(vendor.name);
                              setShowVendorList(false);
                            }}
                            className="flex-1 text-left text-sm hover:text-[#7FA1C3] font-medium"
                          >
                            {vendor.name}
                          </button>
                          <div 
                            onClick={() => {
                              if (window.confirm(`Delete "${vendor.name}" from vendor list?`)) {
                                deleteVendor(vendor.id, vendor.name);
                              }
                            }}
                            className="ml-2 cursor-pointer text-red-500 hover:text-white hover:bg-red-500 p-2 rounded transition-all font-bold text-xl w-8 h-8 flex items-center justify-center"
                            title="Delete vendor"
                            data-testid={`delete-vendor-${vendor.id}`}
                          >
                            ✕
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="date" className="text-sm font-semibold mb-2 block">
                  Date
                </Label>
                <div className="relative">
                  <Input
                    id="date"
                    data-testid="date-input"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="input-focus"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                </div>
              </div>

              <div>
                <Label htmlFor="amount" className="text-sm font-semibold mb-2 block">
                  Amount Submitted (₹)
                </Label>
                <Input
                  id="amount"
                  data-testid="amount-submitted-input"
                  type="number"
                  value={amountSubmitted}
                  onChange={(e) => setAmountSubmitted(e.target.value)}
                  placeholder="Enter total amount"
                  className="input-focus"
                />
              </div>

              {/* UPI/Paytm Amount - Split from total */}
              <div>
                <Label htmlFor="upiAmount" className="text-sm font-semibold mb-2 block">
                  UPI / Paytm Amount (₹)
                </Label>
                <Input
                  id="upiAmount"
                  data-testid="upi-amount-input"
                  type="number"
                  value={upiAmount}
                  onChange={(e) => setUpiAmount(e.target.value)}
                  placeholder="Enter UPI amount (if any)"
                  className="input-focus"
                />
                {amountSubmitted && (
                  <p className="text-xs text-slate-500 mt-1">
                    Cash: ₹{(parseFloat(amountSubmitted) - (parseFloat(upiAmount) || 0)).toFixed(2)}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="commission" className="text-sm font-semibold mb-2 block">
                  Commission Type
                </Label>
                <Select value={commissionType} onValueChange={setCommissionType}>
                  <SelectTrigger id="commission" data-testid="commission-type-select" className="input-focus">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taken">Vendor has taken commission / Paise jama karna h</SelectItem>
                    <SelectItem value="not_taken">Vendor has NOT taken commission / Vendor ne commission nhi liya h</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Label htmlFor="adjustment" className="text-sm font-semibold mb-2 block text-amber-800">
                Previous Balance Adjustment (Optional)
              </Label>
              <p className="text-xs text-amber-700 mb-3">
                If vendor took free items yesterday, enter negative value (e.g., -500). For credit, enter positive value.
              </p>
              <Input
                id="adjustment"
                type="number"
                value={stockAdjustment}
                onChange={(e) => setStockAdjustment(e.target.value)}
                placeholder="Enter adjustment amount (+ or -)"
                data-testid="stock-adjustment-input"
                className="input-focus"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Item Selection
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {Object.entries(allItems)
              .sort((a, b) => parseInt(a[0].replace('₹', '')) - parseInt(b[0].replace('₹', '')))
              .map(([category, categoryItems]) => (
                <div key={category} className="space-y-3">
                  <div className="section-header">{category} Items</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categoryItems.map((item) => {
                      const itemKey = `${item.name}|${item.price}`;
                      const quantity = items[itemKey] || 0;
                      const itemTotal = quantity * item.price;
                      
                      return (
                        <div
                          key={itemKey}
                          className={`p-3 bg-white rounded-lg border transition-colors ${
                            item.isCustom 
                              ? 'border-[#7FA1C3] border-dashed' 
                              : 'border-slate-200 hover:border-[#7FA1C3]'
                          }`}
                        >
                          {/* Item name and badge row */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-semibold text-sm">{item.name}</p>
                              <p className="text-xs text-slate-500">₹{item.price}</p>
                            </div>
                            {item.isCustom && (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] bg-[#7FA1C3] text-white px-1.5 py-0.5 rounded-full">
                                  Custom
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteCustomItem(item.id, item.name);
                                  }}
                                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                                  title={`Delete ${item.name}`}
                                  data-testid={`delete-custom-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Flavour selection for ₹90 items */}
                          {item.price === 90 && (
                            <div className="mb-2">
                              <Select
                                value={itemFlavours[itemKey] || ''}
                                onValueChange={(value) => handleFlavourChange(item.name, item.price, value)}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Select Flavour" />
                                </SelectTrigger>
                                <SelectContent>
                                  {FLAVOUR_OPTIONS.map((flavour) => (
                                    <SelectItem key={flavour} value={flavour}>{flavour}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          
                          {/* Quantity and total row */}
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              value={quantity || ''}
                              onChange={(e) => handleItemQuantityChange(item.name, item.price, e.target.value)}
                              placeholder="Qty"
                              data-testid={`item-quantity-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                              className="w-20 text-center text-sm"
                            />
                            {itemTotal > 0 && (
                              <div className="font-bold text-[#7FA1C3] text-sm">
                                = ₹{itemTotal}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2 text-[#7FA1C3] font-semibold">
                <Plus size={20} />
                <span>Add Custom Item</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Item name"
                  data-testid="custom-item-name-input"
                  className="input-focus"
                />
                <Input
                  type="number"
                  value={newItemPrice}
                  onChange={(e) => setNewItemPrice(e.target.value)}
                  placeholder="Price (₹)"
                  data-testid="custom-item-price-input"
                  className="input-focus"
                />
                <Button
                  onClick={addCustomItem}
                  data-testid="add-custom-item-button"
                  className="bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full btn-hover"
                >
                  <Plus size={18} className="mr-2" />
                  Add
                </Button>
              </div>
            </div>

            {/* Bazaar Items Section - Separate from Invoice */}
            <div className="mt-6 pt-4 border-t-2 border-dashed border-orange-300">
              <div className="flex items-center gap-2 text-orange-600 font-semibold mb-3">
                <Star size={20} />
                <span>Bazaar Items</span>
                <span className="text-xs text-orange-500 font-normal">(Not included in invoice calculation)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {BAZAAR_ITEMS.map((item) => {
                  const itemKey = `${item.name}|${item.price}`;
                  const quantity = bazaarItems[itemKey] || 0;
                  return (
                    <div
                      key={itemKey}
                      className="p-3 bg-orange-50 rounded-lg border border-orange-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-sm">{item.name}</p>
                          <p className="text-xs text-orange-600">₹{item.price}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          value={quantity || ''}
                          onChange={(e) => handleBazaarItemChange(item.name, item.price, e.target.value)}
                          placeholder="Qty"
                          data-testid={`bazaar-item-${item.name.toLowerCase()}`}
                          className="w-20 text-center text-sm"
                        />
                        {quantity > 0 && (
                          <span className="text-sm font-semibold text-orange-600">
                            = ₹{quantity * item.price}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-5">
        <Card className="shadow-lg border-slate-200 sticky top-24">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Invoice Preview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6" id="invoice-preview" data-testid="invoice-preview">
            {calculations ? (
              <div className="space-y-4">
                <div className="text-center pb-4 border-b">
                  <h2 className="text-2xl font-extrabold text-[#7FA1C3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Convella
                  </h2>
                  <p className="text-sm text-slate-600">The Taste of Real Ice Cream</p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-semibold">Vendor:</span>
                    <span data-testid="preview-vendor-name">{vendorName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">Date:</span>
                    <span data-testid="preview-date">{date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">Amount Submitted:</span>
                    <span data-testid="preview-amount-submitted">₹{parseFloat(amountSubmitted).toFixed(2)}</span>
                  </div>
                </div>

                <div className="space-y-2 py-4 border-y">
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Total Sales:</span>
                    <span className="font-bold text-[#7FA1C3]" data-testid="preview-total-sales">
                      ₹{calculations.totalSales.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Vendor Commission:</span>
                    <span className="text-[#F59E0B]" data-testid="preview-vendor-commission">
                      ₹{calculations.vendorCommission.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Factory Share:</span>
                    <span className="text-[#90D26D]" data-testid="preview-factory-share">
                      ₹{calculations.factoryShare.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Stock Limit:</span>
                    <span data-testid="preview-stock-limit">₹{calculations.stockLimit.toFixed(2)}</span>
                  </div>
                  {calculations.adjustment !== 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold">Previous Adjustment:</span>
                        <span className={calculations.adjustment < 0 ? 'text-red-600' : 'text-green-600'}>
                          {calculations.adjustment > 0 ? '+' : ''}₹{calculations.adjustment.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm font-bold">
                        <span className="text-[#7FA1C3]">Adjusted Stock Limit:</span>
                        <span className="text-[#7FA1C3]">₹{calculations.adjustedStockLimit.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>

                {commissionType === 'not_taken' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <p className="font-semibold text-amber-800">
                      Return ₹{calculations.vendorCommission.toFixed(2)} to vendor
                    </p>
                  </div>
                )}

                {calculations.itemsList.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm">Items:</h3>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {calculations.itemsList.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs bg-slate-50 p-2 rounded">
                          <span>
                            {item.item_name}{item.flavour ? ` (${item.flavour})` : ''} x {item.quantity}
                          </span>
                          <span className="font-semibold">₹{item.total}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bazaar Items in Preview - Displayed Separately */}
                {calculations.bazaarItemsList && calculations.bazaarItemsList.length > 0 && (
                  <div className="space-y-2 pt-3 mt-3 border-t border-dashed border-orange-300">
                    <h3 className="font-semibold text-sm text-orange-600 flex items-center gap-1">
                      <Star size={14} />
                      Bazaar Items (Not in Invoice)
                    </h3>
                    <div className="space-y-1">
                      {calculations.bazaarItemsList.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs bg-orange-50 p-2 rounded">
                          <span>{item.item_name} x {item.quantity}</span>
                          <span className="font-semibold text-orange-600">₹{item.total}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-right text-xs text-orange-600 font-semibold">
                      Bazaar Total: ₹{calculations.bazaarItemsList.reduce((sum, item) => sum + item.total, 0)}
                    </div>
                  </div>
                )}

                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Total Stock Value:</span>
                    <span className="font-bold" data-testid="preview-total-stock-value">
                      ₹{calculations.totalStockValue.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`flex justify-between text-sm font-bold ${
                      calculations.balance < 0 ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    <span>Remaining Balance:</span>
                    <span data-testid="preview-balance">₹{calculations.balance.toFixed(2)}</span>
                  </div>
                </div>

                {calculations.balance < 0 && (
                  <div
                    className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800 mb-4"
                    data-testid="warning-exceeded-limit"
                  >
                    ⚠️ Note: Stock value exceeds limit by ₹{Math.abs(calculations.balance).toFixed(2)}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-2 pt-4">
                  {savedInvoiceId && (
                    <Button
                      onClick={resetForm}
                      data-testid="new-invoice-button"
                      className="bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full btn-hover animate-fade-in"
                    >
                      <Plus size={18} className="mr-2" />
                      New Invoice (Reset)
                    </Button>
                  )}
                  <Button
                    onClick={saveInvoice}
                    disabled={savedInvoiceId}
                    data-testid="save-invoice-button"
                    className={`rounded-full btn-hover ${
                      savedInvoiceId
                        ? 'bg-green-500 hover:bg-green-600'
                        : 'bg-[#7FA1C3] hover:bg-[#6482A6]'
                    } text-white`}
                  >
                    <Save size={18} className="mr-2" />
                    {savedInvoiceId ? 'Invoice Saved ✓' : 'Save Invoice'}
                  </Button>
                  {savedInvoiceId && (
                    <Button
                      onClick={printInvoice}
                      data-testid="print-invoice-button"
                      className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full btn-hover animate-fade-in"
                    >
                      <Printer size={18} className="mr-2" />
                      Print Invoice
                    </Button>
                  )}
                  {savedInvoiceId && (
                    <Button
                      onClick={() => {
                        setNewInvoiceDate(date);
                        setShowDateChangeModal(true);
                      }}
                      data-testid="save-different-date-button"
                      className="bg-amber-500 hover:bg-amber-600 text-white rounded-full btn-hover animate-fade-in"
                    >
                      <CalendarDays size={18} className="mr-2" />
                      Save with Different Date
                    </Button>
                  )}
                  <Button
                    onClick={downloadPDF}
                    data-testid="download-pdf-button"
                    className="bg-[#F2C18D] hover:bg-[#E5B07A] text-[#1F2937] rounded-full btn-hover"
                  >
                    <Download size={18} className="mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    onClick={shareWhatsApp}
                    data-testid="share-whatsapp-button"
                    className="bg-[#90D26D] hover:bg-[#7AB856] text-[#064E3B] rounded-full btn-hover"
                  >
                    <Share2 size={18} className="mr-2" />
                    Share via WhatsApp
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <p>Fill in the invoice details to see preview</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Date Change Modal */}
      {showDateChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 shadow-2xl">
            <CardHeader className="bg-amber-50 border-b">
              <CardTitle className="text-xl flex items-center gap-2">
                <CalendarDays className="text-amber-600" />
                Save with Different Date
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Select a new date to save a copy of this invoice. The original invoice will remain unchanged.
              </p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="newDate" className="text-sm font-semibold">New Invoice Date</Label>
                  <Input
                    id="newDate"
                    type="date"
                    value={newInvoiceDate}
                    onChange={(e) => setNewInvoiceDate(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowDateChangeModal(false);
                      setNewInvoiceDate('');
                    }}
                    className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveWithDifferentDate}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                  >
                    <Save size={18} className="mr-2" />
                    Save Invoice
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}